import { Button, Group, Popover, Stack, Text, TextInput } from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import { CalendarDays, Plus, ReceiptText, Search, SlidersHorizontal } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";

import { useMembership } from "../../platform/membership";
import { managesMembers } from "../../platform/permissions";
import { SettingsFrame } from "../../platform/shell/SettingsFrame";
import { Soon } from "../../ui/Soon";
import { InviteForm } from "../../platform/teams/InviteForm";
import { MemberList } from "../../platform/teams/MemberList";
import { MarketingConsent, TeamNameHeader } from "../../platform/teams/TeamHeader";
import { TeamCaseSwitch } from "../../platform/teams/TeamCaseSwitch";

/**
 * Settings' Team tab (design-system.md 3.7): the team's name, the marketing consent, a search
 * over the members, "Add member" as a popover for admins and the owner, and the members table,
 * whose pending invitations they see too (D6: every member sees the members; spec
 * teams/invite-by-email). A status line says what was just sent. Controls follow the
 * role's hints; the API decides. Live's billing controls ("Transactions", the billing period, "Set
 * allowance for all") keep their places as Coming soon until billing exists (decision 58).
 */
export function MembersScreen() {
  const { teamId } = useParams({ from: "/signed-in/teams/$teamId/members" });
  const me = useMembership(teamId);
  const [search, setSearch] = useState("");
  const [inviting, setInviting] = useState(false);
  // Live's toasts as a status line: the app shows no toasts (web/AGENTS.md).
  const [notice, setNotice] = useState<string | undefined>(undefined);
  // The team layout renders this only for a team in the scope.
  if (me === undefined) return null;
  const manages = managesMembers(me.role);
  return (
    <SettingsFrame teamId={teamId}>
      <Stack gap="lg">
        <Stack gap={6}>
          <Group justify="space-between" wrap="nowrap">
            <Group gap={24} align="center">
              <TeamNameHeader teamId={teamId} canRename={manages} />
              <MarketingConsent />
            </Group>
            {manages ? soonButton("Transactions", <ReceiptText size={16} />) : null}
          </Group>
          <Group gap={6} c="var(--cai-color-text-muted)">
            <CalendarDays size={16} aria-hidden />
            <Text size="sm">Billing period: coming soon</Text>
          </Group>
        </Stack>
        <Stack gap="sm">
          <Group justify="space-between" wrap="nowrap">
            <TextInput
              aria-label="Search members"
              placeholder="Search members"
              leftSection={<Search size={16} />}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              w={306}
            />
            {manages ? (
              <Group gap="sm" wrap="nowrap">
                {soonButton("Set allowance for all", <SlidersHorizontal size={16} />)}
                <Popover
                  width={340}
                  position="bottom-end"
                  opened={inviting}
                  onChange={setInviting}
                  trapFocus
                >
                  <Popover.Target>
                    <Button
                      leftSection={<Plus size={16} />}
                      onClick={() => {
                        setNotice(undefined);
                        setInviting((open) => !open);
                      }}
                    >
                      Add member
                    </Button>
                  </Popover.Target>
                  <Popover.Dropdown p={24} aria-label="Add member">
                    <InviteForm
                      teamId={teamId}
                      myRole={me.role}
                      onSent={() => {
                        setInviting(false);
                        setNotice("Invite sent successfully");
                      }}
                      onCancel={() => setInviting(false)}
                    />
                  </Popover.Dropdown>
                </Popover>
              </Group>
            ) : null}
          </Group>
          {notice === undefined ? null : (
            <Text size="sm" component="output">
              {notice}
            </Text>
          )}
          <MemberList teamId={teamId} me={me} search={search} onNotice={setNotice} />
        </Stack>
        {manages ? <TeamCaseSwitch teamId={teamId} /> : null}
      </Stack>
    </SettingsFrame>
  );
}

/** A live billing control we lack yet: in its place, Coming soon. */
function soonButton(label: string, icon: ReactNode) {
  return (
    <Soon>
      {(props) => (
        <Button {...props} variant="default" leftSection={icon}>
          {label}
        </Button>
      )}
    </Soon>
  );
}
